import React, { useState, RefObject } from 'react';
import { X, Mail, Send } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { addNotification } from '../../store/slices/uiSlice';
import { store } from '../../store/store';
import { PhoneInputWithValidation, PhoneValidationResponse } from '../UI/PhoneInputWithValidation';

interface ContactSectionProps {
  visibleSections: Set<string>;
  contactSectionRef: RefObject<HTMLElement>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactSection: React.FC<ContactSectionProps> = ({ visibleSections, contactSectionRef }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; phone?: string }>({});
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (submitError) setSubmitError(null);
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: { email?: string; phone?: string } = {};
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();
    const hasPhone = trimmedPhone.length > 0;

    if (!trimmedEmail) {
      errors.email = 'Please enter your email address.';
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      errors.email = 'Please enter a valid email address.';
    }

    if (hasPhone && !phoneValidation?.valid) {
      errors.phone = phoneValidation?.error || 'Please enter a valid phone number.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await apiClient.post('/contact/', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() ? (phoneValidation?.normalized ?? formData.phone.trim()) : '',
        message: formData.message.trim(),
      });
      setShowSuccessMessage(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      setPhoneValidation(null);
      store.dispatch(
        addNotification({
          type: 'success',
          title: 'Message sent',
          message: "Thank you! We'll reach out to you soon.",
        })
      );
    } catch (err: unknown) {
      const res = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      let message: string;
      if (res && typeof res === 'object') {
        const errObj = res.error ?? res;
        message =
          (typeof errObj === 'object' && errObj !== null && typeof (errObj as { message?: unknown }).message === 'string'
            ? (errObj as { message: string }).message
            : null) ||
          (typeof res.message === 'string' ? res.message : null) ||
          (typeof res.error === 'string' ? res.error : null) ||
          (res.details && typeof res.details === 'object' && Object.values(res.details).flat().length > 0
            ? Object.values(res.details)
                .flat()
                .filter((v): v is string => typeof v === 'string')
                .join(' ')
            : null) ||
          'Something went wrong. Please try again.';
      } else {
        message = 'Something went wrong. Please try again.';
      }
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    setSubmitError(null);
    setFieldErrors({});
    setShowSuccessMessage(false);
    setPhoneValidation(null);
    setIsFormOpen(false);
  };

  return (
    <>
      <section
        id="contact"
        ref={contactSectionRef}
        className="relative z-10 bg-black min-h-[600px] flex items-center justify-center overflow-hidden"
      >
        {/* Gradient overlay - matching theme */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none z-0" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-center">
          <div
            className={`transition-all duration-1000 ${
              visibleSections.has('contact')
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Main Headline */}
            <h2 className="font-megant text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
              <span className="font-megant text-green-400 italic">Let's Create</span>{' '}
              <span className="font-megant text-white">Your Perfect Event</span>
            </h2>

            {/* Sub-headline */}
            <p className="font-montserrat text-white text-base sm:text-lg md:text-xl mb-4 sm:mb-6 max-w-2xl mx-auto px-4 sm:px-0">
              Ready to transform your event planning? Get in touch to begin your journey with us for guest management.
            </p>

            {/* Description */}
            <p className="font-montserrat text-gray-300 text-sm sm:text-base md:text-lg mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Whether you're planning an intimate gathering or a grand celebration, let's discuss how MIRSAL can unlock your event's potential and ensure every detail is perfectly managed.
            </p>

            {/* Get In Touch Button */}
            <button
              onClick={() => setIsFormOpen(true)}
              className="font-montserrat px-6 sm:px-8 py-3 sm:py-4 bg-green-400 hover:bg-green-500 text-black font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-400/50 flex items-center gap-2 mx-auto text-sm sm:text-base"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-montserrat">Get In Touch</span>
            </button>

            <p className="font-montserrat text-gray-400 text-sm sm:text-base mt-4">
              or email us at{' '}
              <a href="mailto:aya@mirsaljo.com" className="text-green-400 hover:text-green-300 underline transition-colors">
                aya@mirsaljo.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Form Modal/Overlay */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-black/80 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide border border-gray-700">
            {/* Close Button */}
            <button
              onClick={handleCancel}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Form Content */}
            <div className="p-6 md:p-8">
              {showSuccessMessage ? (
                <>
                  <div className="text-center py-6">
                    <p className="font-montserrat text-xl text-green-400 font-semibold mb-2">
                      Thank you!
                    </p>
                    <p className="font-montserrat text-white mb-4">
                      Your message has been sent successfully.
                    </p>
                    <p className="font-montserrat text-gray-400">
                      We&apos;ll reach out to you soon.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="font-montserrat w-full px-6 py-3 bg-green-400 hover:bg-green-500 text-black font-semibold rounded-lg transition-all duration-300"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
              <h3 className="font-megant text-2xl md:text-3xl font-bold text-white mb-2">Contact Us</h3>
              <p className="font-montserrat text-gray-400 mb-6">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="font-montserrat block text-sm font-medium text-gray-300 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="font-montserrat w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all"
                    placeholder="Enter your first name"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="font-montserrat block text-sm font-medium text-gray-300 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="font-montserrat w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all"
                    placeholder="Enter your last name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="font-montserrat block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`font-montserrat w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all ${
                      fieldErrors.email ? 'border-red-500' : 'border-gray-700'
                    }`}
                    placeholder="e.g. name@example.com"
                  />
                  {fieldErrors.email && (
                    <p className="font-montserrat text-sm text-red-400 mt-1">{fieldErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="min-h-[6rem]">
                  <label htmlFor="phone" className="font-montserrat block text-sm font-medium text-gray-300 mb-2">
                    Phone <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <PhoneInputWithValidation
                    value={formData.phone}
                    onChange={(phoneValue) => {
                      setFormData((prev) => ({ ...prev, phone: phoneValue }));
                      if (fieldErrors.phone) setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                    }}
                    onValidationChange={setPhoneValidation}
                    error={fieldErrors.phone}
                    placeholder="e.g. 7 1234 5678"
                    showValidationResult={false}
                    required={false}
                    dropdownZIndex={10000}
                    initialCountryCode="JO"
                    className="w-full"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="font-montserrat block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="font-montserrat w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your event or how we can help..."
                  />
                </div>

                {submitError && (
                  <p className="font-montserrat text-sm text-red-400" role="alert">
                    {submitError}
                  </p>
                )}

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="font-montserrat flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="font-montserrat flex-1 px-6 py-3 bg-green-400 hover:bg-green-500 text-black font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                    <span className="font-montserrat">{isSubmitting ? 'Sending…' : 'Send Message'}</span>
                  </button>
                </div>
              </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactSection;

